import "@/pages/ask/ask.css";
import { useCallback, useEffect, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "@/components/application/slideout-menus/slideout-menu";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { AskHandoff, type HandoffPhase } from "@/pages/ask/components/AskHandoff";
import ChatInterface from "@/pages/ask/components/ChatInterface";
import type { ReportRequest } from "@/pages/ask/components/ReportPicker";
import type { Brand } from "@/pages/ask/lib/data";

type AskUnboxDrawerProps = {
  isOpen: boolean;
  /** Changes on every open so the chat remounts and no thread is carried over. */
  sessionKey: number;
  /** Submitted on open. Leave unset to land on the suggestions instead. */
  initialQuestion?: string;
  initialReport?: ReportRequest;
  suggestions?: string[];
  emptyStateTitle?: string;
  onClose: () => void;
  userEmail: string;
  userName?: string;
  scope: Brand;
};

export function AskUnboxDrawer(props: AskUnboxDrawerProps) {
  const redesign = useFeatureFlag("F-ask-drawer-redesign");
  return redesign ? <OverlayDrawer {...props} /> : <BackdropDrawer {...props} />;
}

// Composed from the slideout primitives rather than <SlideoutMenu>, which
// forwards one className to both the overlay and the panel — a width set there
// shrinks the full-screen backdrop and drags the panel off the right edge.
function BackdropDrawer({ isOpen, sessionKey, onClose, ...chat }: AskUnboxDrawerProps) {
  return (
    <ModalOverlay isOpen={isOpen} isDismissable={false} isKeyboardDismissDisabled className="bg-gray-900/85">
      <Modal className="max-w-2xl">
        <Dialog className="gap-0 overflow-hidden bg-unbox-dark-brand-start p-0 ring-0">
          <ChatInterface key={sessionKey} variant="drawer" onClose={onClose} {...chat} />
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function OverlayDrawer({ isOpen, sessionKey, onClose, ...chat }: AskUnboxDrawerProps) {
  const [handoff, setHandoff] = useState<{ key: number; phase: HandoffPhase }>();
  const question = chat.initialQuestion?.trim();

  useEffect(() => {
    if (isOpen && question) setHandoff({ key: sessionKey, phase: "show" });
  }, [isOpen, question, sessionKey]);

  const onPhase = useCallback((phase: HandoffPhase) => setHandoff((h) => h && { ...h, phase }), []);
  const onDone = useCallback(() => setHandoff(undefined), []);
  const phase = handoff?.key === sessionKey ? handoff.phase : undefined;

  return (
    <ModalOverlay
      isOpen={isOpen}
      isDismissable
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className="bg-transparent"
    >
      <Modal className="w-[min(560px,44vw)] max-w-none">
        <Dialog
          aria-label="Ask Unbox"
          className="gap-0 overflow-hidden rounded-bl-2xl bg-transparent p-0 shadow-[-16px_0_48px_-16px_rgba(30,10,60,0.45)] ring-0"
        >
          <div className="ask-new relative size-full overflow-hidden" data-handoff={phase}>
            {phase && question && (
              <AskHandoff key={`handoff-${sessionKey}`} question={question} onPhase={onPhase} onDone={onDone} />
            )}
            <ChatInterface key={sessionKey} variant="drawer" onClose={onClose} {...chat} />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
